import { Calendar as CalendarIcon } from 'lucide-react'
import * as React from 'react'
import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'

import { Button } from '../../primitives/Button'
import { Calendar } from '../../primitives/Calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface DateRangeFilterProps {
  /**
   * Start date (format: YYYY-MM-DD or Date)
   */
  dateFrom: string | Date | null
  /**
   * End date (format: YYYY-MM-DD or Date)
   */
  dateTo: string | Date | null
  /**
   * Callback when start date changes
   */
  onDateFromChange: (_date: string) => void
  /**
   * Callback when end date changes
   */
  onDateToChange: (_date: string) => void
  /**
   * Filter title (optional)
   */
  _title?: string
  /**
   * Button icon (optional)
   */
  icon?: React.ReactNode
  /**
   * Placeholder when no dates are selected
   */
  placeholder?: string
  /**
   * Minimum allowed date
   */
  minDate?: Date
  /**
   * Maximum allowed date
   */
  maxDate?: Date
  /**
   * Disable the filter
   */
  disabled?: boolean
  /**
   * Popover alignment relative to trigger button
   * @default 'end' - maintains current behavior
   */
  popoverAlign?: 'start' | 'center' | 'end'
}

// Date format for display
const DISPLAY_DATE_FORMAT = 'd MMM yyyy'
// Date format for storage (YYYY-MM-DD)
const STORAGE_DATE_FORMAT = 'yyyy-MM-dd'

// Helper to parse date from string
const parseDateFromString = (
  dateString: string | Date | null | undefined
): Date | undefined => {
  if (!dateString) return undefined

  // If it's already a Date object
  if (dateString instanceof Date) {
    return isValid(dateString) ? dateString : undefined
  }

  try {
    // Try parsing as ISO format first
    const isoDate = parseISO(dateString)
    if (isValid(isoDate)) return isoDate
    return undefined
  } catch {
    return undefined
  }
}

// Helper to format date for storage
const formatDateForStorage = (date: Date | undefined): string => {
  if (!date || !isValid(date)) return ''
  return format(date, STORAGE_DATE_FORMAT)
}

/**
 * DateRangeFilter - Date range filter with two calendars (From/To)
 * Reusable component for filtering by date ranges
 */
export function DateRangeFilter({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  icon,
  placeholder = 'Seleccionar fechas',
  minDate,
  maxDate,
  disabled = false,
  popoverAlign = 'end',
}: DateRangeFilterProps) {
  const [activeTab, setActiveTab] = React.useState<'from' | 'to'>('from')
  const [open, setOpen] = React.useState(false)

  const parsedDateFrom = parseDateFromString(dateFrom)
  const parsedDateTo = parseDateFromString(dateTo)

  const handleDateSelect = (date: Date | undefined, type: 'from' | 'to') => {
    const formattedDate = formatDateForStorage(date)
    if (type === 'from') {
      onDateFromChange(formattedDate)
    } else {
      onDateToChange(formattedDate)
    }
  }

  const handleClear = () => {
    onDateFromChange('')
    onDateToChange('')
    setActiveTab('from')
    setOpen(false)
  }

  // Get button label
  const getButtonLabel = (): string => {
    if (parsedDateFrom && parsedDateTo) {
      const fromFormatted = format(parsedDateFrom, DISPLAY_DATE_FORMAT, {
        locale: es,
      })
      const toFormatted = format(parsedDateTo, DISPLAY_DATE_FORMAT, {
        locale: es,
      })
      return `${fromFormatted} - ${toFormatted}`
    }
    if (parsedDateFrom) {
      return `Desde ${format(parsedDateFrom, DISPLAY_DATE_FORMAT, { locale: es })}`
    }
    if (parsedDateTo) {
      return `Hasta ${format(parsedDateTo, DISPLAY_DATE_FORMAT, { locale: es })}`
    }
    return placeholder
  }

  const hasSelection = parsedDateFrom || parsedDateTo

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'h-9 min-w-fit shrink-0 justify-start px-3 font-normal text-[#262626]',
            'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]'
          )}
          disabled={disabled}
          icon={icon || <CalendarIcon className="h-4 w-4" />}
          iconPosition="left"
          size="sm"
          variant="outline"
        >
          <span className="hidden sm:inline">{getButtonLabel()}</span>
          <span className="inline sm:hidden">
            {hasSelection ? 'Fecha' : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={popoverAlign}
        className={cn(
          'w-[calc(100vw-16px)] max-w-[240px] rounded-md border border-[#D4D4D4]',
          'bg-[#FAFAFA] p-0 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]',
          'sm:w-auto'
        )}
        side="bottom"
        sideOffset={4}
      >
        <div className="flex flex-col items-center gap-3 p-2 sm:gap-4">
          {/* Tabs */}
          <div
            className="flex w-full items-center self-stretch rounded-lg bg-[#E5E5E5] p-1"
            style={{ gap: '0px', height: '40px' }}
          >
            {/* Tab Desde */}
            <button
              className={cn(
                'flex h-8 min-h-8 min-w-[29px] flex-1 items-center justify-center gap-2 rounded-md px-2 sm:px-3',
                activeTab === 'from'
                  ? 'bg-[#FAFAFA] shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]'
                  : 'bg-transparent'
              )}
              onClick={() => setActiveTab('from')}
              type="button"
            >
              <span className="text-xs font-medium leading-5 tracking-[0.005em] text-[#262626] sm:text-sm">
                Desde
              </span>
            </button>

            {/* Tab Hasta */}
            <button
              className={cn(
                'flex h-8 min-h-8 min-w-[29px] flex-1 items-center justify-center gap-2 rounded-md px-2 sm:px-3',
                activeTab === 'to'
                  ? 'bg-[#FAFAFA] shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]'
                  : 'bg-transparent'
              )}
              onClick={() => setActiveTab('to')}
              type="button"
            >
              <span className="text-xs font-medium leading-5 tracking-[0.005em] text-[#262626] sm:text-sm">
                Hasta
              </span>
            </button>
          </div>

          {/* Label */}
          <div className="flex w-full items-start justify-start self-stretch">
            <span className="text-sm font-normal leading-5 tracking-[0.005em] text-[#262626]">
              {activeTab === 'from'
                ? 'Seleccione fecha de inicio'
                : 'Seleccione fecha de fin'}
            </span>
          </div>

          {/* Calendar */}
          <Calendar
            className="p-0"
            defaultMonth={
              activeTab === 'from'
                ? parsedDateFrom || new Date()
                : parsedDateTo || new Date()
            }
            disabled={(date) => {
              if (disabled) return true
              if (minDate && date < startOfDay(minDate)) return true
              if (maxDate && date > endOfDay(maxDate)) return true

              // If we're on "to", disable dates before "from"
              if (
                activeTab === 'to' &&
                parsedDateFrom &&
                date < startOfDay(parsedDateFrom)
              ) {
                return true
              }

              // If we're on "from", disable dates after "to"
              if (
                activeTab === 'from' &&
                parsedDateTo &&
                date > endOfDay(parsedDateTo)
              ) {
                return true
              }

              return false
            }}
            key={activeTab}
            locale={es}
            onSelect={(date) => handleDateSelect(date, activeTab)}
            selected={activeTab === 'from' ? parsedDateFrom : parsedDateTo}
          />

          {/* Clear Button */}
          <Button
            className={cn(
              'h-9 w-full min-w-[36px] justify-center rounded-md px-4',
              'border-0 bg-[#FAFAFA] shadow-none',
              'hover:bg-[#F5F5F5] active:bg-[#FAFAFA]'
            )}
            onClick={handleClear}
            size="sm"
            type="button"
            variant="outline"
          >
            <span className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
              Limpiar selección
            </span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
